import { faqsPl } from "@data/gps-sensors-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";
import {
  Calendar,
  CheckCircle,
  Cloud,
  Code,
  Cpu,
  FileText,
  FlaskConical,
  LayoutDashboard,
  LifeBuoy,
  MapPin,
  PenTool,
  Radio,
  Rocket,
  Search,
  Smartphone,
  Thermometer,
  Watch,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Lokalizatory GPS i czujniki IoT - trackery, sensory i dashboard";

export const description =
  "Lokalizatory GPS, GPS trackery, czujniki IoT i dashboard monitoringu — budujemy urządzenie z firmware i panelem w jednym projekcie. Od trackera z aplikacją Flutter po dashboard do danych z ESP32 i Arduino/MQTT.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Lokalizatory GPS i czujniki IoT",
    description,
    "GPS tracking and IoT sensor development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Lokalizatory GPS",
  headerTail: "i czujniki IoT",
  description:
    "Budujemy GPS trackery, czujniki IoT i dashboard monitoringu — urządzenie, firmware i panel w jednym projekcie. Od lokalizatora z aplikacją mobilną po sensory jakości powietrza ESP32 z danymi w chmurze AWS IoT.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "GPS tracker + app + dashboard", Icon: CheckCircle },
    { text: "LTE-M / NB-IoT i BLE", Icon: CheckCircle },
    { text: "Sensory i panel w jednym zespole", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo są lokalizatory GPS i czujniki IoT?",
    p: "W zapytaniach klienci proszą o urządzenie i panel monitoringu razem — mapa, historia tras, alerty i wykresy sensorów. Nie rozdzielamy trackera, czujnika i dashboardu na trzy osobne produkty. Czy któryś z tych scenariuszy pasuje do Ciebie?",
  },
  points: [
    {
      Icon: MapPin,
      title: "Budujesz GPS tracker z aplikacją mobilną",
      description:
        "Lokalizator GPS z firmware, aplikacją Flutter (iOS/Android) i backendem — mapa, historia tras, alerty geofencing i zarządzanie flotą w jednym produkcie.",
    },
    {
      Icon: Thermometer,
      title: "Wdrażasz czujniki jakości powietrza lub środowiskowe",
      description:
        "Monitorowanie CO₂, PM2.5, temperatury i wilgotności — ESP32 z firmware, wysyłka danych do chmury i dashboard z wykresami i alertami progowymi.",
    },
    {
      Icon: LayoutDashboard,
      title: "Masz sensory i potrzebujesz dashboardu IoT",
      description:
        "Urządzenia ESP32, Arduino lub MQTT już wysyłają dane — budujemy warstwę odbioru, wykresy, alerty i panel administracyjny pod istniejące czujniki.",
    },
    {
      Icon: Watch,
      title: "Projektujesz wearable z lokalizacją",
      description:
        "Opaska, pendant albo urządzenie antykradzieżowe z GPS, BLE finder network lub LTE-M — firmware, aplikacja mobilna i backend w jednym projekcie.",
    },
    {
      Icon: Rocket,
      title: "Potrzebujesz MVP trackera lub sensora na szybko",
      description:
        "Wąski PoC — lokalizacja lub odczyt sensora plus prosty panel — często w 6–12 tygodni, zanim zainwestujesz w obudowę produkcyjną i certyfikację.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o trackerze, czujniku albo dashboardzie — nawet jeśli masz tylko wymagania biznesowe. Zaproponujemy zakres MVP.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować trackery i czujniki IoT z nami?",
    p: "Lokalizator GPS bez dashboardu albo czujnik bez panelu to połowa produktu. Budujemy urządzenie, firmware, aplikację i dashboard IoT razem — bo w leadach klienci proszą właśnie o taki komplet.",
    imageAlt: "Lokalizatory GPS, czujniki IoT i dashboard monitoringu",
  },
  values: [
    {
      Icon: MapPin,
      title: "GPS tracker z mapą, alertami i flotą",
      description:
        "Lokalizator GPS to nie tylko współrzędne — budujemy mapę, historię tras, geofencing, alerty i zarządzanie wieloma urządzeniami w jednym dashboardzie IoT.",
    },
    {
      Icon: Radio,
      title: "Łączność dopasowana do scenariusza",
      description:
        "LTE-M / NB-IoT do długiej pracy na baterii, LTE/GSM gdy potrzeba częstszych update'ów, BLE gdy tracker współpracuje z telefonem — dobieramy stack do use case'u.",
    },
    {
      Icon: Thermometer,
      title: "Czujniki IoT z danymi w chmurze",
      description:
        "ESP32, moduły przemysłowe, Modbus/CAN — integrujemy sensory z MQTT, AWS IoT Core lub własnym API i budujemy dashboard z wykresami i alertami.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna do trackera i sensorów",
      description:
        "Flutter lub natywne iOS/Android — podgląd lokalizacji, danych sensorów, konfiguracja urządzenia i powiadomienia push w jednej aplikacji.",
    },
    {
      Icon: LayoutDashboard,
      title: "Dashboard IoT gotowy do operacji",
      description:
        "Webowy panel z mapą, wykresami historycznymi, alertami progowymi i zarządzaniem użytkownikami — centrum monitoringu floty trackerów i sensorów.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Lokalizatory GPS, czujniki IoT i dashboard",
    p: "Od GPS trackera z aplikacją po dashboard do istniejących sensorów — budujemy urządzenie i warstwę software w jednym projekcie, nie jako trzy osobne zlecenia.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza scenariusza i dobór łączności",
      description:
        "Mapujemy wymagania — zasięg, częstotliwość update'ów, bateria, flota — i dobieramy GPS/GNSS, LTE-M, NB-IoT, BLE lub kombinację pod realny use case.",
    },
    {
      Icon: Cpu,
      title: "Hardware trackera lub czujnika IoT",
      description:
        "Projektujemy PCB z modułem GNSS i łącznością LPWA albo integrujemy gotowe moduły — od PoC na DevKit po własną płytkę pod produkcję.",
    },
    {
      Icon: Code,
      title: "Firmware lokalizatora i sensorów",
      description:
        "Pozycjonowanie GPS/GNSS, odczyt sensorów, oszczędzanie energii, buforowanie danych offline i wysyłka telemetryczna do chmury.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna (Flutter)",
      description:
        "Aplikacja do trackera lub czujnika — mapa, podgląd danych, konfiguracja urządzenia, powiadomienia i parowanie BLE.",
    },
    {
      Icon: LayoutDashboard,
      title: "Dashboard IoT i panel monitoringu",
      description:
        "Webowy panel z mapą floty, wykresami sensorów, alertami, historią danych i zarządzaniem użytkownikami — gotowy do operacji biznesowych.",
    },
    {
      Icon: Cloud,
      title: "Backend i integracja AWS IoT / MQTT",
      description:
        "Serwer odbierający dane z trackerów i sensorów, retencja, alerty, API i integracja z AWS IoT Core lub istniejącą infrastrukturą klienta.",
    },
    {
      Icon: FlaskConical,
      title: "Dashboard do istniejących czujników",
      description:
        "Masz już ESP32, Arduino lub urządzenia MQTT — budujemy warstwę odbioru danych, wykresy, alerty i panel administracyjny bez przebudowy hardware.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie floty i kolejne wersje",
      description:
        "Monitoring urządzeń w terenie, OTA firmware, rozbudowa dashboardu i wsparcie po wdrożeniu trackera lub sieci sensorów.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz nam tracker, czujnik albo dashboard — nawet jeśli masz tylko pomysł biznesowy. Zaproponujemy wąski PoC.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje tracker GPS lub czujnik IoT z dashboardem",
    p: "Od konsultacji po działający lokalizator GPS lub sensor z panelem monitoringu. Przejrzyste etapy i szybkie iteracje.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem. Opisujesz tracker, czujnik lub dashboard — dostajesz propozycję łączności, architektury i orientacyjną wycenę.",
    },
    {
      Icon: FileText,
      title: "Specyfikacja i plan MVP",
      duration: "w kilka dni",
      description:
        "Definiujemy wąski zakres PoC — np. lokalizacja + mapa + alerty albo sensor + wykres + próg alarmowy. Otrzymujesz harmonogram i widełki budżetowe.",
    },
    {
      Icon: PenTool,
      title: "NDA i umowa",
      duration: "ustalenie harmonogramu",
      description:
        "Podpisujemy NDA, ustalamy kamienie milowe i sposób testowania urządzenia w terenie.",
    },
    {
      Icon: Cpu,
      title: "Hardware i firmware",
      duration: "min. 4–8 tygodni",
      description:
        "Budujemy tracker lub czujnik — moduł GNSS, sensory, firmware z oszczędzaniem energii i wysyłką danych do chmury.",
    },
    {
      Icon: LayoutDashboard,
      title: "Dashboard i aplikacja mobilna",
      duration: "równolegle z hardware",
      description:
        "Panel webowy i aplikacja mobilna powstają równolegle z urządzeniem — integracja od pierwszych dni, nie na końcu projektu.",
    },
    {
      Icon: FlaskConical,
      title: "Testy w terenie",
      duration: "2–4 tygodnie",
      description:
        "Testujemy lokalizację, łączność LPWA, zużycie baterii i poprawność danych na dashboardzie w realnych warunkach.",
    },
    {
      Icon: Rocket,
      title: "Wdrożenie i skalowanie floty",
      duration: "min. 2 tygodnie",
      description:
        "Przygotowujemy infrastrukturę pod większą flotę trackerów lub sensorów, OTA i monitoring operacyjny.",
    },
    {
      Icon: CheckCircle,
      title: "Utrzymanie i rozwój",
      duration: "na stałe",
      description:
        "Wsparcie po premierze — nowe funkcje dashboardu, poprawki firmware i rozbudowa sieci urządzeń.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów tracker GPS lub czujnik IoT",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — lokalizatory GPS i czujniki IoT",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o lokalizatory GPS i czujniki IoT",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Budujesz tracker GPS lub czujnik IoT?",
    p: "Umów się na darmową konsultację — opowiedz o urządzeniu, dashboardzie i aplikacji, a zaproponujemy plan MVP.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Lokalizatory GPS, GPS trackery, czujniki IoT i dashboard monitoringu — urządzenie z panelem w jednym projekcie.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/iot", text: "Systemy IoT" },
      ...iotClusterLinks.filter(
        l =>
          l.href !== "/iot" && l.href !== "/lokalizatory-gps-i-czujniki-iot",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
